import {
    FetchState,
    Advice3,
    AdvisorDeliveryPage,
    Advisor3State,
    AdvisorEventType,
    ActionHistory
} from "@/shared/types";
import * as advisorService from "@/shared/api/advisor3Service";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useImpersonatorStore } from "@/shared/store/impersonator";

export const initialState: Advisor3State = {
    fetchState: FetchState.Uninitialized,
    advices: [],
    bannerAdvice: null
};

export const useUserAdvisorStore = defineStore("userAdvisor", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        advicesGetter () {
            const custodianSettingsStore = useCustodianSettingsStore();
            if (!custodianSettingsStore || custodianSettingsStore.userCustodianSettings.displayAdvice) {
                return this.advices;
            }

            return [];
        },
        bannerAdvice3 () {
            const custodianSettingsStore = useCustodianSettingsStore();
            if (!custodianSettingsStore
                || custodianSettingsStore.userCustodianSettings.displayAdvice) {
                return this.bannerAdvice;
            }
            return null;
        }
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setAdvice (advice: Advice3[]) {
            this.advices = [ ...advice ];
        },
        setBannerAdvice (advice: Advice3) {
            this.bannerAdvice = advice;
        },
        removeAdvice (data: { actionId: string, ruleExecutionId: number }) {
            const index = this.advices.findIndex(e => e.actionId === data.actionId && e.ruleExecutionId === data.ruleExecutionId);
            if (index < 0) return;
            this.advices.splice(index, 1);
        },
        loadAdvice3 (pageName: string): Promise<any> {
            if (this.fetchState === FetchState.Loading) {
                return Promise.resolve();
            }
            const custodianSettingsStore = useCustodianSettingsStore();
            if (custodianSettingsStore &&
                    !custodianSettingsStore.userCustodianSettings.displayAdvice) {
                this.setFetchState(FetchState.Loaded);
                return Promise.resolve();
            }

            if (this.fetchState === FetchState.Loaded) {
                const advice = this.advices
                    .filter(x => x.deliveryPage === mapDeliveryPage(pageName) && x.isNew)
                    .sort(x => x.priority)[0];
                this.setBannerAdvice(advice);
                if (advice) {
                    this.saveAdviceHistoryEvent(advice);
                }
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);

            return advisorService.getAdvice()
                .then(response => {
                    const advices = response.data;
                    this.setAdvice(advices);
                    const advice = advices
                        .filter(x => x.deliveryPage === mapDeliveryPage(pageName) && x.isNew)
                        .sort(x => x.priority)[0];
                    this.setBannerAdvice(advice);
                    if (advice) {
                        this.saveAdviceHistoryEvent(advice);
                    }
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    this.setFetchState(FetchState.Loaded);
                });
        },
        dismissAdvice (data: { actionId: string, ruleExecutionId: number }) {
            return advisorService.dismissAdvice(data)
                .then(() => {
                    this.removeAdvice(data);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    this.setFetchState(FetchState.Loaded);
                });
        },
        savePageViewEvent (page: string) {
            const eventType = mapPageViewEvent(page);
            if (eventType === AdvisorEventType.None) {
                return;
            }
            this.saveEvent(eventType);
        },
        saveEvent (eventType: AdvisorEventType) {
            const impersonationStore = useImpersonatorStore();
            if (impersonationStore.isActive) {
                return;
            }
            return advisorService.saveEvent(eventType)
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        saveAdviceHistoryEvent (advice: Advice3) {
            return advisorService.saveActionHistory({
                    actionId: advice.actionId,
                    ruleExecutionId: advice.ruleExecutionId
                } as ActionHistory)
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
    }
});

function mapDeliveryPage (value) {
    switch (value) {
        case "Dashboard":
            return AdvisorDeliveryPage.Dashboard;
        case "HealthcareExpenses":
            return AdvisorDeliveryPage.HealthcareExpense;
        case "CashBalance":
            return AdvisorDeliveryPage.Cash;
        case "InvestmentBalance":
            return AdvisorDeliveryPage.Investment;
        case "ReimburseMe":
            return AdvisorDeliveryPage.Reimburse;
        case "Contributions":
            return AdvisorDeliveryPage.Contributions;
        case "ContributionPlanner":
            return AdvisorDeliveryPage.ContributionPlanner;
        case "Resources":
            return AdvisorDeliveryPage.Resources;
        case "Settings":
            return AdvisorDeliveryPage.Settings;
        default:
            return AdvisorDeliveryPage.None;
    }
}

function mapPageViewEvent (value) {
    switch (value) {
        case "Dashboard":
            return AdvisorEventType.DashboardView;
        case "HealthcareExpenses":
            return AdvisorEventType.HealthcareExpenseView;
        case "CashBalance":
            return AdvisorEventType.CashView;
        case "InvestmentBalance":
            return AdvisorEventType.InvestmentView;
        case "ReimburseMe":
            return AdvisorEventType.ReimburseView;
        case "Contributions":
            return AdvisorEventType.ContributionsView;
        case "ContributionPlanner":
            return AdvisorEventType.ContributionPlannerView;
        case "Resources":
            return AdvisorEventType.ResourcesView;
        case "Settings":
            return AdvisorEventType.SettingsView;
        default:
            return AdvisorEventType.None;
    }
}
